import { useEffect, type RefObject } from "react";
import { focusableElements, ownedOverlays } from "@/components/overlay";

const dialogs: { id: string; element: HTMLElement }[] = [];
const originals = new Map<
    HTMLElement,
    { inert: boolean; hidden: string | null }
>();
let bodyOverflow = "";

const isolate = (): void => {
    for (const [element, original] of originals) {
        element.inert = original.inert;
        if (original.hidden === null) {
            element.removeAttribute("aria-hidden");
        } else {
            element.setAttribute("aria-hidden", original.hidden);
        }
    }
    const top = dialogs.at(-1);
    if (!top) {
        originals.clear();
        return;
    }
    const allowed = [top.element, ...ownedOverlays(top.id)];
    for (const element of Array.from(document.body.children)) {
        if (
            !(element instanceof HTMLElement) ||
            element.matches("[data-live-region]") ||
            allowed.some((root) => element.contains(root))
        ) {
            continue;
        }
        if (!originals.has(element)) {
            originals.set(element, {
                inert: element.inert,
                hidden: element.getAttribute("aria-hidden"),
            });
        }
        element.inert = true;
        element.setAttribute("aria-hidden", "true");
    }
};

export const useModalFocus = (
    open: boolean,
    id: string,
    ref: RefObject<HTMLDivElement | null>
): void => {
    useEffect(() => {
        const element = ref.current;
        if (!open || !element) {
            return;
        }
        const previous = document.activeElement;
        if (!dialogs.length) {
            bodyOverflow = document.body.style.overflow;
        }
        dialogs.push({ id, element });
        document.body.style.overflow = "hidden";
        const focusFirst = (): void => {
            const preferred = element.querySelector<HTMLElement>(
                "[autofocus],input:not([disabled]),textarea:not([disabled])"
            );
            (preferred || focusableElements(element)[0] || element).focus();
        };
        focusFirst();
        isolate();
        const observer = new MutationObserver(isolate);
        observer.observe(document.body, { childList: true });
        const contain = (event: FocusEvent): void => {
            if (dialogs.at(-1)?.id !== id) {
                return;
            }
            const target = event.target as Node;
            if (
                ![element, ...ownedOverlays(id)].some((root) =>
                    root.contains(target)
                )
            ) {
                focusFirst();
            }
        };
        const tab = (event: KeyboardEvent): void => {
            if (
                event.key !== "Tab" ||
                event.defaultPrevented ||
                dialogs.at(-1)?.id !== id
            ) {
                return;
            }
            const roots = [element, ...ownedOverlays(id)];
            const items = roots.flatMap(focusableElements);
            if (!items.length) {
                event.preventDefault();
                element.focus();
                return;
            }
            const index = items.indexOf(document.activeElement as HTMLElement);
            const next = event.shiftKey ? index - 1 : index + 1;
            if (index === -1 || next < 0 || next >= items.length) {
                event.preventDefault();
                (event.shiftKey ? items.at(-1) : items[0])?.focus();
            }
        };
        document.addEventListener("focusin", contain);
        window.addEventListener("keydown", tab);
        return () => {
            observer.disconnect();
            document.removeEventListener("focusin", contain);
            window.removeEventListener("keydown", tab);
            const index = dialogs.findIndex((dialog) => dialog.id === id);
            if (index >= 0) {
                dialogs.splice(index, 1);
            }
            isolate();
            if (!dialogs.length) {
                document.body.style.overflow = bodyOverflow;
            }
            if (
                previous instanceof HTMLElement &&
                previous.isConnected &&
                !previous.closest("[inert]")
            ) {
                previous.focus();
            }
        };
    }, [open, id, ref]);
};
